import { Button } from '@framework/ui/atoms/Button';
import { useCurrentUserEntity } from '@user/UserEntity/useCurrentUserEntity';
import { useCallback, useState } from 'react';
import { UserSurveyOperation } from './UserSurveyOperation';
import { useGroupEntity } from '@group/hooks';
import { useTranslation } from 'react-i18next';

const GOOGLE_FORM_INFO = {
  ACTION_URL:
    'https://docs.google.com/forms/u/0/d/e/1FAIpQLScWx8vX0yu2zR2HIggSiFaH5vGJGDD6imt8EnQa66J2PtcjFQ/formResponse',
  SURVEY_ID_ID: '1378400464',
  GROUP_ID_ID: '693742132',
  GROUP_NAME_ID: '2062509255',
  USER_ID_ID: '337104524',
  USER_NAME_ID: '1582786260',
  EMAIL_ADDRESS_ID: '1824839066',
  POSITION_ID: '816142724',
  INDUSTRY_ID: '186008090',
  SIZE_OF_ORG_ID: '1049683192',
  PURPOSE_OF_USE_ID: '1376855717',
  EXPECTATION_ON_BALUS_ID: '711912707',
};

const FIELD_INFO = {
  surveyId: { name: `entry.${GOOGLE_FORM_INFO.SURVEY_ID_ID}` },
  groupId: { name: `entry.${GOOGLE_FORM_INFO.GROUP_ID_ID}` },
  groupName: { name: `entry.${GOOGLE_FORM_INFO.GROUP_NAME_ID}` },
  userId: { name: `entry.${GOOGLE_FORM_INFO.USER_ID_ID}` },
  userName: { name: `entry.${GOOGLE_FORM_INFO.USER_NAME_ID}` },
  emailAddress: { name: `entry.${GOOGLE_FORM_INFO.EMAIL_ADDRESS_ID}` },
  position: { name: `entry.${GOOGLE_FORM_INFO.POSITION_ID}` },
  industry: { name: `entry.${GOOGLE_FORM_INFO.INDUSTRY_ID}` },
  sizeOfOrg: { name: `entry.${GOOGLE_FORM_INFO.SIZE_OF_ORG_ID}` },
  purposeOfUse: { name: `entry.${GOOGLE_FORM_INFO.PURPOSE_OF_USE_ID}` },
  expectationOnBalus: { name: `entry.${GOOGLE_FORM_INFO.EXPECTATION_ON_BALUS_ID}` },
};

// このアンケートでは、UI に表示するラベル文言（翻訳ファイル ja.jsonc / en.jsonc 経由）と、
// Google Forms に送信する値（DICT の value）を意図的に分離している。
//
// - DICT の value: Google Forms 集計の主キー。過去回答との集計整合性を保つため、UI 言語に
//   関係なく常に固定の日本語文字列を送信する。値を変更すると過去データとの集計が崩れるため編集禁止。
//
// - 翻訳ファイル (auth.survey.{category}.{code}): UI 表示文言。UX のために言い回しを磨く
//   余地を残してあり、DICT 側の送信値とは独立に変更してよい。
//
// 同じ日本語が両方に並ぶことになるが、これは関心分離のための意図的な並走であり、
// 「片方に寄せる」リファクタを行ってはならない。
//
// 各 DICT のキー (code) は対応する翻訳キー segment と一致させてあり、各 DICT 直下の
// X_I18N_KEYS から完全 i18n キーを引いて t(...) に渡す。`blank` は空オプションのため
// 翻訳を引かず空文字を返す (X_I18N_KEYS の satisfies 制約からも除外している)。
const POSITION_DICT = {
  blank: '',
  owner: '経営層（経営者・役員）',
  manager: '管理職（部門長・マネージャーなど）',
  generalist: '一般職（担当者・スタッフ）',
  specialist: '営業・販売・マーケティング技術職（エンジニア・IT関連など）',
  designer: '企画・クリエイティブ（デザイン・プランニングなど）',
  admin: '管理部門（人事・総務・労務・法務など）',
  accounting: '経営企画・財務・経理',
  cs: '顧客対応（カスタマーサポート・カスタマーサクセスなど）',
  freelancer: '自営業・フリーランス',
  others: 'その他',
};

const POSITION_I18N_KEYS = {
  owner: 'auth.survey.position.owner',
  manager: 'auth.survey.position.manager',
  generalist: 'auth.survey.position.generalist',
  specialist: 'auth.survey.position.specialist',
  designer: 'auth.survey.position.designer',
  admin: 'auth.survey.position.admin',
  accounting: 'auth.survey.position.accounting',
  cs: 'auth.survey.position.cs',
  freelancer: 'auth.survey.position.freelancer',
  others: 'auth.survey.position.others',
} as const satisfies Record<Exclude<keyof typeof POSITION_DICT, 'blank'>, string>;

const INDUSTRY_DICT = {
  blank: '',
  it: 'IT・情報通信',
  media: '広告・出版・メディア',
  financial: '金融・保険',
  manufacturing: '製造・ものづくり',
  wholesale: '卸売・小売・流通',
  educational: '教育・学習支援',
  medical: '医療・福祉',
  construction: '建設・不動産',
  service: '飲食・宿泊・サービス業',
  public: '公共・官公庁・団体',
  entertainment: 'エンターテインメント・クリエイティブ',
  freelancer: '個人事業主・フリーランス',
  others: 'その他',
};

const INDUSTRY_I18N_KEYS = {
  it: 'auth.survey.industry.it',
  media: 'auth.survey.industry.media',
  financial: 'auth.survey.industry.financial',
  manufacturing: 'auth.survey.industry.manufacturing',
  wholesale: 'auth.survey.industry.wholesale',
  educational: 'auth.survey.industry.educational',
  medical: 'auth.survey.industry.medical',
  construction: 'auth.survey.industry.construction',
  service: 'auth.survey.industry.service',
  public: 'auth.survey.industry.public',
  entertainment: 'auth.survey.industry.entertainment',
  freelancer: 'auth.survey.industry.freelancer',
  others: 'auth.survey.industry.others',
} as const satisfies Record<Exclude<keyof typeof INDUSTRY_DICT, 'blank'>, string>;

const SIZE_OF_ORG_DICT = {
  blank: '',
  size1: '1人（個人・フリーランス）',
  size2To10: '2〜10人',
  size11To50: '11〜50人',
  size51To200: '51〜200人',
  size201To500: '201〜500人',
  size501To1000: '501〜1,000人',
  sizeOver1000: '1,001人以上',
  unknown: 'わからない／回答しない',
};

const SIZE_OF_ORG_I18N_KEYS = {
  size1: 'auth.survey.orgSize.size1',
  size2To10: 'auth.survey.orgSize.size2To10',
  size11To50: 'auth.survey.orgSize.size11To50',
  size51To200: 'auth.survey.orgSize.size51To200',
  size201To500: 'auth.survey.orgSize.size201To500',
  size501To1000: 'auth.survey.orgSize.size501To1000',
  sizeOver1000: 'auth.survey.orgSize.sizeOver1000',
  unknown: 'auth.survey.orgSize.unknown',
} as const satisfies Record<Exclude<keyof typeof SIZE_OF_ORG_DICT, 'blank'>, string>;

const PURPOSE_OF_USE_DICT = {
  taskManagement: 'タスク・プロジェクトの整理',
  visualizeIdea: 'アイデア出し・発想の可視化',
  sharing: 'チーム内の情報共有・ドキュメント管理',
  knowledgeManagement: '知識の整理・ナレッジ管理',
  illustrate: '図解・マップ作成（フロー図・マインドマップなど）',
  collaboration: '社内外とのコラボレーション',
  meeting: '会議・ワークショップの進行・可視化',
  presentation: '資料・プレゼンテーションの作成',
  reflection: 'チームコミュニケーションの活性化（ふりかえりなど）',
  others: 'その他（自由記述）',
};

const PURPOSE_OF_USE_I18N_KEYS = {
  taskManagement: 'auth.survey.purpose.taskManagement',
  visualizeIdea: 'auth.survey.purpose.visualizeIdea',
  sharing: 'auth.survey.purpose.sharing',
  knowledgeManagement: 'auth.survey.purpose.knowledgeManagement',
  illustrate: 'auth.survey.purpose.illustrate',
  collaboration: 'auth.survey.purpose.collaboration',
  meeting: 'auth.survey.purpose.meeting',
  presentation: 'auth.survey.purpose.presentation',
  reflection: 'auth.survey.purpose.reflection',
  others: 'auth.survey.purpose.others',
} as const satisfies Record<keyof typeof PURPOSE_OF_USE_DICT, string>;

const EXPECTATION_ON_BALUS_DICT = {
  handleComplexity: '複雑な情報や状況を整理したい',
  understandCustomer: '顧客理解を深めたい',
  diveIntoNewRealm: '新しい領域・テーマを理解したい',
  identifyIssue: '問題・課題を特定したい',
  goalSetting: '意義のある目標を設定したい',
  planning: 'チームやプロジェクトの方針を立てたい',
  evolveProduct: 'プロダクトやサービスを進化させたい',
  reconsiderBiz: '自社ビジネスを見直したい／変革したい',
  organizationReform: '組織のあり方を変えたい',
  deepenThought: '一人で思考を整理したい',
  accelerateTeamwork: 'チームや組織に合ったツールを選定したい',
  others: 'その他（自由記述）',
};

const EXPECTATION_ON_BALUS_I18N_KEYS = {
  handleComplexity: 'auth.survey.expectation.handleComplexity',
  understandCustomer: 'auth.survey.expectation.understandCustomer',
  diveIntoNewRealm: 'auth.survey.expectation.diveIntoNewRealm',
  identifyIssue: 'auth.survey.expectation.identifyIssue',
  goalSetting: 'auth.survey.expectation.goalSetting',
  planning: 'auth.survey.expectation.planning',
  evolveProduct: 'auth.survey.expectation.evolveProduct',
  reconsiderBiz: 'auth.survey.expectation.reconsiderBiz',
  organizationReform: 'auth.survey.expectation.organizationReform',
  deepenThought: 'auth.survey.expectation.deepenThought',
  accelerateTeamwork: 'auth.survey.expectation.accelerateTeamwork',
  others: 'auth.survey.expectation.others',
} as const satisfies Record<keyof typeof EXPECTATION_ON_BALUS_DICT, string>;

type Props = {
  groupId: string;
  userId: string;
  surveyId: string;
  onAnswerCompleted(): void;
  onCancel(): void;
};

export const UserSurveyForm: React.FC<Props> = ({ groupId, userId, surveyId, onAnswerCompleted, onCancel }: Props) => {
  const { t } = useTranslation();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [position, setPosition] = useState<string>('');
  const [industry, setIndustry] = useState<string>('');
  const [sizeOfOrg, setSizeOfOrg] = useState<string>('');
  const [purposeOfUse, setPurposeOfUse] = useState<string>('');
  const [expectationOnBalus, setExpectationOnBalus] = useState<string>('');

  const [purposeOfUseChecked, setPurposeOfUseChecked] = useState<boolean[]>(
    new Array(Object.keys(PURPOSE_OF_USE_DICT).length).fill(false)
  );
  const [expectationOnBalusChecked, setExpectationOnBalusChecked] = useState<boolean[]>(
    new Array(Object.keys(EXPECTATION_ON_BALUS_DICT).length).fill(false)
  );

  const [purposeOfUseOthers, setPurposeOfUseOthers] = useState<string>('');
  const [expectationOnBalusOthers, setExpectationOnBalusOthers] = useState<string>('');

  const userEntity = useCurrentUserEntity();
  const { group } = useGroupEntity(groupId);

  const handleChangePosition = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setPosition(event.target.value);
  }, []);

  const handleChangeIndustry = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setIndustry(event.target.value);
  }, []);

  const handleChangeSizeOfOrg = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSizeOfOrg(event.target.value);
  }, []);

  const mergePurposeOfUseValues = (status: boolean[], othersValue: string) => {
    const merged = Object.values(PURPOSE_OF_USE_DICT)
      .filter((_, index) => status[index])
      .map((value) => {
        if (value === PURPOSE_OF_USE_DICT.others) {
          return `${value}: ${othersValue}`;
        } else {
          return value;
        }
      })
      .join('\n');

    return merged;
  };

  // 利用目的チェックボックス
  const handleChangePurposeOfUse = useCallback(
    (changeIndex: number) => {
      const changedStatus = purposeOfUseChecked.map((checked, index) => {
        return index === changeIndex ? !checked : checked;
      });
      setPurposeOfUseChecked(changedStatus);
      setPurposeOfUse(mergePurposeOfUseValues(changedStatus, purposeOfUseOthers));
    },
    [purposeOfUseChecked, purposeOfUseOthers]
  );

  // 利用目的「その他（自由記述）」テキストボックス
  const handleChangePurposeOfUseOthers = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const othersValue = event.target.value;

      setPurposeOfUseOthers(othersValue);
      setPurposeOfUse(mergePurposeOfUseValues(purposeOfUseChecked, othersValue));
    },
    [purposeOfUseChecked]
  );

  const mergeExpectationOnBalusValues = (status: boolean[], othersValue: string) => {
    const merged = Object.values(EXPECTATION_ON_BALUS_DICT)
      .filter((_, index) => status[index])
      .map((value) => {
        if (value === EXPECTATION_ON_BALUS_DICT.others) {
          return `${value}: ${othersValue}`;
        } else {
          return value;
        }
      })
      .join('\n');

    return merged;
  };

  // Balusから得られることへの期待チェックボックス
  const handleChangeExpectationOnBalus = useCallback(
    (changeIndex: number) => {
      const changedStatus = expectationOnBalusChecked.map((checked, index) => {
        return index === changeIndex ? !checked : checked;
      });
      setExpectationOnBalusChecked(changedStatus);
      setExpectationOnBalus(mergeExpectationOnBalusValues(changedStatus, expectationOnBalusOthers));
    },
    [expectationOnBalusChecked, expectationOnBalusOthers]
  );

  // Balusから得られることへの期待「その他（自由記述）」テキストボックス
  const handleChangeExpectationOnBalusOthers = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const othersValue = event.target.value;

      setExpectationOnBalusOthers(othersValue);
      setExpectationOnBalus(mergeExpectationOnBalusValues(expectationOnBalusChecked, othersValue));
    },
    [expectationOnBalusChecked]
  );

  const handleSubmit = useCallback(async () => {
    const status = await UserSurveyOperation.answer(userId);
    if (status === 'ReadyToAnswer') {
      setIsSubmitted(true);

      return true;
    } else {
      return false;
    }
  }, [userId]);

  const isInputCompleted = useCallback(() => {
    return !!position && !!industry && !!sizeOfOrg && !!purposeOfUse && !!expectationOnBalus;
  }, [position, industry, sizeOfOrg, purposeOfUse, expectationOnBalus]);

  const onIframeLoaded = useCallback(() => {
    if (isSubmitted) {
      onAnswerCompleted();
    }
  }, [isSubmitted, onAnswerCompleted]);

  // 各カテゴリの code → 表示文言は、対応する X_I18N_KEYS 経由の静的参照で解決する。
  // 動的参照 (t(code, { keyPrefix }) やテンプレートリテラル形式の t() 呼び出し) を使わないことで、
  // 個別の完全 i18n キーが文字列リテラルとしてソースに乗り、check-unused-locales.ts が
  // 個別キー単位で参照を追跡できる (DICT に対応しない孤立翻訳キーを未使用として検出可能)。
  const positionLabel = useCallback(
    (code: keyof typeof POSITION_DICT): string => (code === 'blank' ? '' : t(POSITION_I18N_KEYS[code])),
    [t]
  );
  const industryLabel = useCallback(
    (code: keyof typeof INDUSTRY_DICT): string => (code === 'blank' ? '' : t(INDUSTRY_I18N_KEYS[code])),
    [t]
  );
  const orgSizeLabel = useCallback(
    (code: keyof typeof SIZE_OF_ORG_DICT): string => (code === 'blank' ? '' : t(SIZE_OF_ORG_I18N_KEYS[code])),
    [t]
  );
  const purposeLabel = useCallback(
    (code: keyof typeof PURPOSE_OF_USE_DICT): string => t(PURPOSE_OF_USE_I18N_KEYS[code]),
    [t]
  );
  const expectationLabel = useCallback(
    (code: keyof typeof EXPECTATION_ON_BALUS_DICT): string => t(EXPECTATION_ON_BALUS_I18N_KEYS[code]),
    [t]
  );

  const createPurposeOfUseCheckbox = (code: string, submittedValue: string, displayLabel: string, index: number) => (
    <label className="mb-2 block w-[440px]" htmlFor={code} key={code}>
      <input
        id={code}
        type="checkbox"
        key={code}
        value={submittedValue}
        className={'form-checkbox mr-4'}
        checked={purposeOfUseChecked[index]}
        onChange={() => handleChangePurposeOfUse(index)}
      />
      {displayLabel}
    </label>
  );

  const createExpectationOnBalusCheckbox = (
    code: string,
    submittedValue: string,
    displayLabel: string,
    index: number
  ) => (
    <label className="mb-2 block w-[340px]" htmlFor={code} key={code}>
      <input
        id={code}
        type="checkbox"
        key={code}
        value={submittedValue}
        className={'form-checkbox mr-4'}
        checked={expectationOnBalusChecked[index]}
        onChange={() => handleChangeExpectationOnBalus(index)}
      />
      {displayLabel}
    </label>
  );

  return (
    <form action={GOOGLE_FORM_INFO.ACTION_URL} method="POST" target="thanks_iframe" onSubmit={handleSubmit}>
      <iframe name="thanks_iframe" style={{ display: 'none' }} onLoad={onIframeLoaded} />

      <input type="hidden" name={FIELD_INFO.surveyId.name} value={surveyId} />
      <input type="hidden" name={FIELD_INFO.groupId.name} value={groupId} />

      {group && <input type="hidden" name={FIELD_INFO.groupName.name} value={group.name.toString()} />}
      <input type="hidden" name={FIELD_INFO.userId.name} value={userId} />

      {userEntity && (
        <>
          <input type="hidden" name={FIELD_INFO.userName.name} value={userEntity.name} />
          <input type="hidden" name={FIELD_INFO.emailAddress.name} value={userEntity.mailAddress} />
        </>
      )}

      <input type="hidden" name={FIELD_INFO.purposeOfUse.name} value={purposeOfUse} />
      <input type="hidden" name={FIELD_INFO.expectationOnBalus.name} value={expectationOnBalus} />

      <div style={{ width: '1280px', height: '520px' }}>
        <div className="flex flex-col px-8 pb-8">
          <div className="flex flex-row">
            <div>
              <div className="w-4/5 py-2 font-bold">{t('auth.survey.field.position')}</div>
              <div>
                <select
                  name={FIELD_INFO.position.name}
                  onChange={handleChangePosition}
                  value={position}
                  className="borderfocus:outline-none form-select border-brand"
                >
                  {(Object.entries(POSITION_DICT) as [keyof typeof POSITION_DICT, string][]).map(
                    ([code, submittedValue]) => (
                      <option key={code} value={submittedValue}>
                        {positionLabel(code)}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
            <div className="pl-14">
              <div className="w-4/5 py-2 font-bold">{t('auth.survey.field.industry')}</div>
              <div>
                <select
                  name={FIELD_INFO.industry.name}
                  onChange={handleChangeIndustry}
                  value={industry}
                  className="borderfocus:outline-none form-select border-brand"
                >
                  {(Object.entries(INDUSTRY_DICT) as [keyof typeof INDUSTRY_DICT, string][]).map(
                    ([code, submittedValue]) => (
                      <option key={code} value={submittedValue}>
                        {industryLabel(code)}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
            <div className="pl-10">
              <div className="w-4/5 py-2 font-bold">{t('auth.survey.field.sizeOfOrg')}</div>
              <div>
                <select
                  name={FIELD_INFO.sizeOfOrg.name}
                  onChange={handleChangeSizeOfOrg}
                  value={sizeOfOrg}
                  className="borderfocus:outline-none form-select border-brand"
                >
                  {(Object.entries(SIZE_OF_ORG_DICT) as [keyof typeof SIZE_OF_ORG_DICT, string][]).map(
                    ([code, submittedValue]) => (
                      <option key={code} value={submittedValue}>
                        {orgSizeLabel(code)}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-row">
            <div>
              <div className="pb-4 pt-6 font-bold">{t('auth.survey.field.purposeOfUse')}</div>
              <div>
                {(Object.entries(PURPOSE_OF_USE_DICT) as [keyof typeof PURPOSE_OF_USE_DICT, string][]).map(
                  ([code, submittedValue], index) =>
                    createPurposeOfUseCheckbox(code, submittedValue, purposeLabel(code), index)
                )}
                <input
                  type="text"
                  className="w-[518px] rounded-sm border p-2"
                  value={purposeOfUseOthers}
                  onChange={handleChangePurposeOfUseOthers}
                />
              </div>
            </div>
            <div className="pl-14">
              <div className="pb-4 pt-6 font-bold">{t('auth.survey.field.expectation')}</div>
              <div>
                {
                  (
                    Object.entries(EXPECTATION_ON_BALUS_DICT) as [keyof typeof EXPECTATION_ON_BALUS_DICT, string][]
                  ).reduce(
                    ([prev, total], [code, submittedValue], index) => {
                      const checkbox = createExpectationOnBalusCheckbox(
                        code,
                        submittedValue,
                        expectationLabel(code),
                        index
                      );
                      if (index <= 3) {
                        // 最初の２段はチェックボックスを横並びにする
                        if (index % 2 === 0) {
                          return [checkbox, total];
                        } else {
                          return [
                            <></>,
                            <>
                              {total}
                              <div key={`div_${index}`} className="flex flex-row">
                                {prev}
                                {checkbox}
                              </div>
                            </>,
                          ];
                        }
                      } else {
                        return [
                          <></>,
                          <>
                            {total}
                            {checkbox}
                          </>,
                        ];
                      }
                    },
                    [<></>, <></>]
                  )[1]
                }
                <input
                  type="text"
                  className="w-[518px] rounded-sm border p-2"
                  value={expectationOnBalusOthers}
                  onChange={handleChangeExpectationOnBalusOthers}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={'flex justify-end py-2'}>
        <div className={'pr-2'}>
          <Button type="submit" color="primary" disabled={!isInputCompleted()} loading={isSubmitted}>
            {t('auth.survey.action.submit')}
          </Button>
        </div>
        <div className={'pr-2'}>
          <Button type="button" color="secondary" onClick={onCancel}>
            {t('common.action.close')}
          </Button>
        </div>
      </div>
    </form>
  );
};
